import React, { useState, useEffect } from 'react';
import SEO from '../SEO'; // Import the SEO component

// Import technology images
import steamSterilizationImg from '../assets/Technology/IMG_5561.jpg';
import millingImg from '../assets/Technology/IMG_5563.jpg';
import preCuttingImg from '../assets/Technology/11.jpeg';
import fineGrindingImg from '../assets/Technology/13.jpeg';
import coolingImg from '../assets/Technology/IMG_4260.jpg';
import featuresBg from '../assets/Technology/download.jpeg';
import placeholderImage from '../assets/Technology/download.jpeg';

function Technology() {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isManual, setIsManual] = useState(false);

    const technologies = [
        { name: 'VaporClean Technology', image: steamSterilizationImg, alt: 'Steam sterilization equipment for spice purification' },
        { name: 'Precision Grinding', image: millingImg, alt: 'Precision grinding machinery for spices' },
        { name: 'Initial Sizing', image: preCuttingImg, alt: 'Pre-cutting equipment for spice sizing' },
        { name: 'Ultra-Fine Milling', image: fineGrindingImg, alt: 'Ultra-fine milling system for spice powders' },
        { name: 'ChillGuard System', image: coolingImg, alt: 'Cooling system to preserve spice oils' }
    ];

    useEffect(() => {
        if (isManual) return; // Skip auto-cycle if user has interacted manually
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % technologies.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [technologies.length, isManual]);

    const handlePrev = () => {
        setIsManual(true);
        setCurrentImageIndex((prevIndex) => (prevIndex - 1 + technologies.length) % technologies.length);
    };

    const handleNext = () => {
        setIsManual(true);
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % technologies.length);
    };

    // SEO Structured Data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Technology - Galle Ceylon Spices International",
        "description": "Discover the advanced technologies used by Galle Ceylon Spices International for spice processing, including VaporClean, Precision Grinding, and ChillGuard systems.",
        "url": "https://www.galleceylonspices.com/technology",
        "image": technologies[0].image,
        "mainEntity": {
            "@type": "ItemList",
            "name": "Spice Processing Technologies",
            "itemListElement": technologies.map((tech, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": tech.name,
                "description": `Advanced technology for ${tech.name.toLowerCase()} at Galle Ceylon Spices International.`
            })).concat([
                {
                    "@type": "ListItem",
                    "position": technologies.length + 1,
                    "name": "Advanced Spice Purification",
                    "description": "Innovative European systems for spice heating, vapor treatment, and rapid cooling."
                },
                {
                    "@type": "ListItem",
                    "position": technologies.length + 2,
                    "name": "Optimal Moisture Management",
                    "description": "RapidCool technology for instant cooling to preserve aromatic oils."
                }
            ])
        },
        "publisher": {
            "@type": "Organization",
            "name": "Galle Ceylon Spices International (Pvt) Ltd",
            "logo": {
                "@type": "ImageObject",
                "url": "%PUBLIC_URL%/logo.png"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "448/2, Nawadagala",
                "addressLocality": "Elpitiya",
                "postalCode": "80000",
                "addressCountry": "Sri Lanka"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+94-77-929-1829",
                "contactType": "Customer Service",
                "contactOption": "WhatsApp",
                "areaServed": "Worldwide",
                "availableLanguage": "English"
            }
        }
    };

    if (!technologies.length) {
        return (
            <div className="container my-4 py-4">
                <SEO
                    title="Technology - Galle Ceylon Spices International"
                    description="Discover the advanced technologies used by Galle Ceylon Spices International for spice processing."
                    keywords="spice processing, Ceylon spices, Galle Ceylon Spices, spice technology, VaporClean, Precision Grinding"
                    canonicalUrl="https://www.galleceylonspices.com/technology"
                    ogImage="%PUBLIC_URL%/logo.png"
                />
                <h2 style={{ fontFamily: 'Georgia', color: '#355E3B', fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>
                    Technology Unavailable
                </h2>
                <p style={{ fontFamily: 'Georgia', color: '#666', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
                    Unable to load technology data. Please try again later or contact <a href="mailto:galleceylonspices@gmail.com" style={{ color: '#C19A6B' }}>support</a>.
                </p>
            </div>
        );
    }

    return (
        <div className="container my-4 py-4">
            <SEO
                title="Technology - Galle Ceylon Spices International"
                description="Discover the advanced technologies used by Galle Ceylon Spices International for spice processing, including VaporClean, Precision Grinding, and ChillGuard systems."
                keywords="spice processing, Ceylon spices, Galle Ceylon Spices, spice technology, VaporClean, Precision Grinding, ChillGuard"
                canonicalUrl="https://www.galleceylonspices.com/technology"
                ogImage={technologies[0].image || '%PUBLIC_URL%/logo.png'}
                structuredData={structuredData}
            />
            <section className="animate__animated animate__fadeIn" style={{ animationDuration: '1s' }}>
                <h2
                    className="text-center mb-4 position-relative"
                    style={{
                        fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                        letterSpacing: '0.5px',
                        color: '#355E3B',
                        fontFamily: 'Georgia',
                        lineHeight: '1.2'
                    }}
                >
                    Technology We Use
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '-8px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '80px',
                            height: '2px',
                            backgroundColor: '#C19A6B',
                            borderRadius: '2px'
                        }}
                    />
                </h2>

                <div className="row align-items-center">
                    {/* Left Side - Image Carousel */}
                    <div
                        className="col-12 col-md-6 mb-4 animate__animated animate__fadeInUp"
                        style={{ animationDelay: '0.2s', animationDuration: '0.8s' }}
                    >
                        <div
                            className="card h-100 shadow-sm border-0 position-relative carousel-card"
                            style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid rgba(193, 154, 107, 0.3)'
                            }}
                            role="region"
                            aria-label="Technology image carousel"
                        >
                            <div
                                style={{
                                    padding: '15px',
                                    height: 'clamp(250px, 40vw, 400px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <img
                                    src={technologies[currentImageIndex].image}
                                    alt={technologies[currentImageIndex].alt}
                                    className="img-fluid"
                                    style={{
                                        transition: 'opacity 0.5s ease-in-out',
                                        objectFit: 'contain',
                                        width: '100%',
                                        height: '100%'
                                    }}
                                    onError={(e) => (e.target.src = placeholderImage)}
                                    loading="lazy"
                                />
                            </div>
                            {/* Left Arrow */}
                            <button
                                onClick={handlePrev}
                                className="position-absolute carousel-button"
                                aria-label="Previous technology image"
                                style={{
                                    left: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: '#C19A6B',
                                    border: 'none',
                                    color: '#FFFFFF',
                                    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                            >
                                ←
                            </button>
                            {/* Right Arrow */}
                            <button
                                onClick={handleNext}
                                className="position-absolute carousel-button"
                                aria-label="Next technology image"
                                style={{
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    backgroundColor: '#C19A6B',
                                    border: 'none',
                                    color: '#FFFFFF',
                                    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                                    width: '30px',
                                    height: '30px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer'
                                }}
                            >
                                →
                            </button>
                        </div>
                    </div>

                    {/* Right Side - Technology Points */}
                    <div
                        className="col-12 col-md-6 mb-4 animate__animated animate__fadeInUp"
                        style={{ animationDelay: '0.4s', animationDuration: '0.8s' }}
                    >
                        <div
                            className="card h-100 shadow-sm p-3"
                            style={{
                                borderRadius: '8px',
                                border: '1px solid rgba(193, 154, 107, 0.3)',
                                backgroundColor: '#FFFFFF'
                            }}
                        >
                            <h4
                                style={{
                                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                                    fontFamily: 'Georgia',
                                    color: '#355E3B',
                                    fontWeight: 'bold',
                                    marginBottom: '1rem',
                                    borderBottom: '2px solid #C19A6B',
                                    paddingBottom: '5px'
                                }}
                            >
                                Our Technologies
                            </h4>
                            <ul className="list-unstyled">
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>VaporClean Technology:</strong> Galle Ceylon Spices uses an advanced EU-patented system for effective and safe purification, preserving essential oils in spices.
                                </li>
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>Precision Grinding:</strong> State-of-the-art equipment ensures the highest standards of hygiene and spice quality during processing.
                                </li>
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>Initial Sizing:</strong> Provides a variety of particle sizes (0.5mm – 20mm) with optimal output for diverse spice applications.
                                </li>
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>Ultra-Fine Milling:</strong> Delivers a broad spectrum of fine spice powders (60-2500 Mesh) for premium quality products.
                                </li>
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>ChillGuard System:</strong> Minimizes essential oil loss during grinding with strategically placed magnetic strips and enhanced controls.
                                </li>
                                <li
                                    className="mb-2"
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>Automated Operations:</strong> Fully managed through advanced automation systems for seamless production.
                                </li>
                                <li
                                    style={{
                                        fontFamily: 'Georgia',
                                        color: '#355E3B',
                                        fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                        lineHeight: '1.6'
                                    }}
                                >
                                    <strong>High-Capacity Setup:</strong> Designed to process large quantities of raw spices efficiently.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section with Background Image */}
            <div
                className="py-4 my-4 rounded-3 shadow-lg"
                style={{
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${featuresBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    color: 'white'
                }}
            >
                <div className="container">
                    <h2
                        className="text-center mb-4"
                        style={{ fontFamily: 'Georgia', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)' }}
                    >
                        Features
                    </h2>

                    <div className="row">
                        <div className="col-12 col-lg-6 mb-3">
                            <div className="p-3 bg-dark bg-opacity-75 rounded-3">
                                <h3
                                    className="mb-3"
                                    style={{ fontFamily: 'Georgia', fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)' }}
                                >
                                    Advanced Spice Purification
                                </h3>
                                <ul className="list-unstyled">
                                    <li
                                        className="mb-2"
                                        style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}
                                    >
                                        <strong>Innovative European Systems</strong> for spice heating, vapor treatment, and rapid cooling (CeylonPure and SpiceShield technologies).
                                    </li>
                                    <li
                                        className="mb-2"
                                        style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}
                                    >
                                        <strong>Smart Automation Controls</strong> managing processing time, spice temperature, vapor levels, and output flow, ensuring customized treatment for various spices based on client needs.
                                    </li>
                                    <li style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                                        <strong>Uniform and Effective Processing</strong> through an advanced blending mechanism for consistent quality.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="col-12 col-lg-6 mb-3">
                            <div className="p-3 bg-dark bg-opacity-75 rounded-3">
                                <h3
                                    className="mb-3"
                                    style={{ fontFamily: 'Georgia', fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)' }}
                                >
                                    Optimal Moisture Management for Spices
                                </h3>
                                <ul className="list-unstyled">
                                    <li
                                        className="mb-2"
                                        style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}
                                    >
                                        <strong>RapidCool Technology</strong> for instant cooling, creating a thermal shock that reduces microbial content, preserves aromatic oils, and ensures safe packaging of spices.
                                    </li>
                                    <li
                                        className="mb-2"
                                        style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}
                                    >
                                        <strong>Low-Loss Processing</strong> with minimal impact on essential oils and overall quality (leading in the industry).
                                    </li>
                                    <li style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)' }}>
                                        <strong>Preservation of Spice Characteristics</strong> (crucial for delicate spices with specific aroma and flavor requirements).
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inline CSS for styles and responsiveness */}
            <style>
                {`
                    .carousel-card {
                        transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
                    }
                    .carousel-card:hover {
                        transform: scale(1.02);
                        box-shadow: 0 6px 15px rgba(193, 154, 107, 0.3);
                    }
                    .carousel-button {
                        transition: background-color 0.3s ease-in-out, transform 0.3s ease-in-out;
                    }
                    .carousel-button:hover, .carousel-button:focus {
                        background-color: #A67B4D !important;
                        transform: translateY(-50%) scale(1.1);
                    }
                    @media (max-width: 576px) {
                        .container.my-4 {
                            margin-top: 1rem !important;
                            margin-bottom: 1rem !important;
                        }
                        .container.py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1rem !important;
                        }
                        .carousel-card {
                            height: 200px !important;
                        }
                        .carousel-button {
                            font-size: 0.9rem !important;
                            width: 25px !important;
                            height: 25px !important;
                        }
                        h2 {
                            font-size: clamp(1.5rem, 3.5vw, 2rem) !important;
                        }
                        h3 {
                            font-size: clamp(1rem, 2vw, 1.2rem) !important;
                        }
                        ul li {
                            font-size: clamp(0.8rem, 1.8vw, 1rem) !important;
                        }
                    }
                    @media (max-width: 576px) and (orientation: landscape) {
                        .carousel-card {
                            height: 150px !important;
                        }
                        .carousel-button {
                            font-size: 0.8rem !important;
                            width: 20px !important;
                            height: 20px !important;
                        }
                    }
                    @media (min-width: 577px) and (max-width: 768px) {
                        .container.my-4 {
                            margin-top: 2rem !important;
                            margin-bottom: 2rem !important;
                        }
                        .container.py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .py-4 {
                            padding-top: 1.5rem !important;
                            padding-bottom: 1.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 1.5rem !important;
                        }
                        .carousel-card {
                            height: 250px !important;
                        }
                    }
                    @media (min-width: 769px) and (max-width: 1024px) {
                        .container.my-4 {
                            margin-top: 3rem !important;
                            margin-bottom: 3rem !important;
                        }
                        .container.py-4 {
                            padding-top: 3rem !important;
                            padding-bottom: 3rem !important;
                        }
                        .py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2rem !important;
                        }
                        .carousel-card {
                            height: 300px !important;
                        }
                    }
                    @media (min-width: 1025px) {
                        .container.my-4 {
                            margin-top: 4rem !important;
                            margin-bottom: 4rem !important;
                        }
                        .container.py-4 {
                            padding-top: 4rem !important;
                            padding-bottom: 4rem !important;
                        }
                        .py-4 {
                            padding-top: 2.5rem !important;
                            padding-bottom: 2.5rem !important;
                        }
                        .mb-4 {
                            margin-bottom: 2.5rem !important;
                        }
                        .carousel-card {
                            height: 400px !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}

export default Technology;